import { createDefaultPanel } from 'features/charts-v2/store'
import {
  type ChartQueryPanel,
  toQueryPanel,
} from 'features/charts-v2/types/chart-query-panel'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { Condition } from 'features/charts-v2/types/yogiql'
import {
  EMPTY_YOGIQL_NAMES,
  fromYogiQLFilters,
  toYogiQLFilters,
  warnYogiQLFallback,
} from 'features/charts-v2/utils'
// bully ignore[no-cross-feature-private-imports: the panel->wire projection charts-v2 owns (SEMANTIC marker injection); every page that sends a panel imports it from charts-v2/utils and no public-seam re-export exists yet. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { injectSemanticIntoPanel } from 'features/charts-v2/utils/inject-semantic-into-panel'
import { EMPTY_CATALOG, leafFields } from 'features/feedback/feedback-query'
import { FeedbackResponse } from 'features/feedback/types'
// bully ignore[no-cross-feature-private-imports: same inherited reviews-only bridge already used by SummarizeAccordion/feedback-filter-sidebar-handlers; no public-seam re-export exists yet. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import {
  applyFeedbackRequestReviewsOnly,
  applyFeedbackRequestReviewsOnlyToFilters,
  detectScoreReviewType,
  ScoreReviewType,
} from 'features/filters/context/reviews-only-criterion'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'
import type { Filters, SummaryRequest } from 'types/project'

export enum SEARCH_MODE {
  POST_NORMAL = 0,
  POST_TEXT_NORMAL = 1,
  POST_TEXT_KEYWORDS = 2,
}

export const mergeFeedbackResponse = (
  data: FeedbackResponse,
  toMerge: FeedbackResponse,
): FeedbackResponse => {
  data.posts = [...data.posts, ...toMerge.posts]
  return data
}

/**
 * Reviews-only-normalizes a panel's filters for the summary/summarize request
 * boundaries. `isUnsupportedScore` is true for a Ratings Only panel (no review
 * text to summarize) — callers should pass `undefined` instead of
 * `effectiveFilters` to the request/action in that case (`handleSummarize`
 * no-ops on `undefined`). Shared by `FeedbackSummary` (per panel, for the
 * stats/breakdown data) and `Container` (active panel only, for the command
 * bar's Summarize button) so both stay in sync with the charts↔feedback
 * reviews-only contract in `reviews-only-criterion.ts`.
 */
export function deriveFeedbackReviewsOnlyFilters(
  panelFilters: Filters | undefined,
  reviewsOnly: boolean,
): { effectiveFilters: Filters | undefined; isUnsupportedScore: boolean } {
  const scoreReviewType = panelFilters
    ? detectScoreReviewType(panelFilters)
    : undefined
  const isUnsupportedScore =
    reviewsOnly && scoreReviewType === ScoreReviewType.RatingsOnly
  const effectiveFilters =
    reviewsOnly && panelFilters
      ? applyFeedbackRequestReviewsOnly(panelFilters)
      : panelFilters
  return { effectiveFilters, isUnsupportedScore }
}

/**
 * The v2 `panel` both feedback request boundaries (post-previews and
 * summary-panel) send: the store's panel lowered to wire fields (the `SEMANTIC`
 * marker written in, then narrowed to the query fields), with the reviews-only
 * score normalization applied when reviews-only is on. Returns a copy; the
 * store's panel is never mutated.
 */
export const buildFeedbackRequestPanel = (
  panel: ChartPanel,
  reviewsOnly: boolean,
): ChartQueryPanel => {
  const queryPanel = toQueryPanel(injectSemanticIntoPanel(panel))
  return reviewsOnly
    ? {
        ...queryPanel,
        filters: applyFeedbackRequestReviewsOnlyToFilters(queryPanel.filters),
      }
    : queryPanel
}

const RATING_FIELDS = new Set(['rating', 'rating_only'])

const isRatingCondition = (c: Condition): boolean => {
  const fields = leafFields(c)
  return fields.length > 0 && fields.every((f) => RATING_FIELDS.has(f))
}

/** `buildFeedbackRequestPanel`'s reviews-only rewrite for held YogiQL
 *  conditions: only the rating conditions are rewritten, by the same rules;
 *  the rest go as they are. */
export function withFeedbackReviewsOnly(
  filters: Condition[],
  reviewsOnly: boolean,
): Condition[] {
  if (!reviewsOnly) return filters
  const rating = filters.filter(isRatingCondition)
  const read = fromYogiQLFilters(rating, [])
  if (!read.ok) {
    warnYogiQLFallback(`feedback reviews-only not applied: ${read.reason}`)
    return filters
  }
  // No `rating_only` is "Reviews & Ratings" (0); the rules spell null `Null`.
  const typed = read.fields.filters.map((f) => ({
    ...f,
    values: f.values.map((v) => (v === ALIAS_FOR_NULL_VALUES ? 'Null' : v)),
    valueType: f.valueType ?? 0,
  }))
  const panel: ChartPanel = {
    ...createDefaultPanel(0),
    ...read.fields,
    filters: applyFeedbackRequestReviewsOnlyToFilters(typed),
  }
  const normalized = toYogiQLFilters(panel, EMPTY_CATALOG, EMPTY_YOGIQL_NAMES)
  if (typeof normalized === 'string') {
    warnYogiQLFallback(`feedback reviews-only not applied: ${normalized}`)
    return filters
  }
  return [...filters.filter((c) => !isRatingCondition(c)), ...normalized]
}

/** The Summarize drawer's request: `panel` drives the legacy routes, `held`
 *  (reviews-only normalized) the YogiQL ones; the v1 lists are display-only. */
export const feedbackSummaryRequest = (
  projectId: string,
  filterValues: Filters,
  panel: ChartQueryPanel,
  held: Condition[] | undefined,
  reviewsOnly: boolean,
): SummaryRequest => ({
  headers: { proj_uuid: projectId },
  body: {
    panel,
    ...(held
      ? { yogiqlFilters: withFeedbackReviewsOnly(held, reviewsOnly) }
      : {}),
    chart_id: '5_5_7_rating_sentiment',
    tier1: 'Brand',
    tier1_value: '',
    tier2_value: null,
    tier3_value: null,
    search_terms: filterValues.searchQuery,
    search_criteria: filterValues.searchCondition,
    criteria: filterValues.values,
    search_terms_logical: filterValues.search_terms_logical,
  },
})
